import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, Loader2, Search, Send, CheckCircle2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';
import { HcFaqSuggestions } from './HcFaqSuggestions';

const FaqItem = ({ f, open, onToggle }) => (
  <div className="border-b border-gray-100 last:border-0" data-testid={`hc-faq-${f.id}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 text-left p-4 sm:p-5 hover:bg-gray-50 transition-colors" aria-expanded={open} data-testid={`hc-faq-toggle-${f.id}`}>
      <span className="font-semibold text-[#363636]">{f.question}</span>
      <ChevronDown className={`w-5 h-5 shrink-0 text-[#922A8C] transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="px-4 sm:px-5 pb-5 text-gray-600 whitespace-pre-line text-sm leading-relaxed" data-testid={`hc-faq-answer-${f.id}`}>{f.answer}</div>}
  </div>
);

const STATUS = { open: 'bg-amber-100 text-amber-800', resolved: 'bg-green-100 text-green-800' };

const ContactForm = ({ data, onSent }) => {
  const [form, setForm] = useState({ topic: '', message: '', contact_method: 'email', contact_value: data.contact_defaults.email || '' });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const method = (m) => setForm((f) => ({ ...f, contact_method: m, contact_value: (m === 'email' ? data.contact_defaults.email : data.contact_defaults.phone) || '' }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.topic) return toast.error('Choose a topic');
    if (form.message.trim().length < 5) return toast.error('Tell us a little more about what you need');
    setSending(true);
    try {
      const { data: r } = await hcApi.post('/support/requests', form);
      toast.success(`Request ${r.number} sent. Our team will contact you soon.`);
      setForm((f) => ({ ...f, topic: '', message: '' }));
      onSent(r);
    } catch (err) {
      toast.error(hcError(err));
    } finally {
      setSending(false);
    }
  };
  const solved = (f) => {
    hcApi.post(`/support/faqs/${f.id}/solved`).catch(() => {});
    setForm((x) => ({ ...x, topic: '', message: '' }));
    toast.success("Glad that helped! You don't need to send a request.");
  };
  const field = 'w-full rounded-xl border border-gray-200 bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40';
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="hc-support-form">
      <label className="block text-sm font-semibold text-[#363636]">What do you need help with?
        <select value={form.topic} onChange={(e) => set('topic', e.target.value)} className={`${field} h-12 mt-1 font-normal`} data-testid="hc-support-topic">
          <option value="">Choose a topic</option>
          {data.topics.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="block text-sm font-semibold text-[#363636]">Your message
        <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={5} maxLength={3000} placeholder="Tell us what's happening"
          className={`${field} py-3 mt-1 font-normal`} data-testid="hc-support-message" />
      </label>
      <HcFaqSuggestions faqs={data.faqs} text={form.message} onSolved={solved} />
      <div>
        <div className="text-sm font-semibold text-[#363636] mb-1">How should we contact you?</div>
        <div className="flex gap-2 mb-2">
          {['email', 'phone'].map((m) => (
            <button type="button" key={m} onClick={() => method(m)} data-testid={`hc-support-method-${m}`}
              className={`h-10 px-5 rounded-full text-sm font-semibold transition-colors ${form.contact_method === m ? 'bg-[#922A8C] text-white' : 'bg-white border border-gray-200 text-gray-600'}`}>
              {m === 'email' ? 'Email' : 'Phone'}
            </button>
          ))}
        </div>
        <input value={form.contact_value} onChange={(e) => set('contact_value', e.target.value)} className={`${field} h-12`}
          placeholder={form.contact_method === 'email' ? 'you@example.com' : '(615) 555-0100'} data-testid="hc-support-contact-value" />
      </div>
      <button type="submit" disabled={sending} className="w-full sm:w-auto h-12 px-8 rounded-xl bg-[#922A8C] text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#7a2275] disabled:opacity-60" data-testid="hc-support-submit">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Request
      </button>
    </form>
  );
};

export default function HcSupportPage() {
  const loc = useLocation();
  const contactRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => { hcApi.get('/support').then(({ data: d }) => setData(d)).catch((e) => setError(hcError(e))); }, []);
  useEffect(() => {
    if (data && loc.hash === '#contact') setTimeout(() => contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [data, loc.hash]);

  const term = q.trim().toLowerCase();
  const faqs = (data?.faqs || []).filter((f) => !term || `${f.question} ${f.answer}`.toLowerCase().includes(term));

  return (
    <HcLayout>
      <div className="max-w-6xl hc-fade-up min-w-0" data-testid="hc-support-page">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]">Support</h1>
        <p className="text-gray-500 mt-2">Find quick answers, or send a request to our help desk.</p>
        {!data && !error && <Loader2 className="w-8 h-8 animate-spin text-[#922A8C] mt-6" />}
        {error && <p className="text-red-600 mt-6" data-testid="hc-support-error">{error}</p>}
        {data && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-6 mt-6">
            <section className="min-w-0">
              <h2 className="text-base md:text-lg font-bold text-[#363636] mb-3">Frequently Asked Questions</h2>
              <div className="relative mb-3">
                <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions"
                  className="w-full h-12 pl-12 pr-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40" data-testid="hc-faq-search" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="hc-faq-list">
                {faqs.length === 0
                  ? <p className="p-6 text-gray-500 text-sm" data-testid="hc-faq-empty">{data.faqs.length ? 'No questions match your search.' : 'Answers to common questions will appear here soon.'}</p>
                  : faqs.map((f) => <FaqItem key={f.id} f={f} open={openId === f.id} onToggle={() => setOpenId(openId === f.id ? null : f.id)} />)}
              </div>
            </section>
            <aside className="min-w-0 space-y-6">
              <section ref={contactRef} id="contact" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 scroll-mt-24" data-testid="hc-support-contact">
                <h2 className="text-base md:text-lg font-bold text-[#363636] flex items-center gap-2 mb-1"><LifeBuoy className="w-5 h-5 text-[#922A8C]" />Contact the Help Desk</h2>
                <p className="text-sm text-gray-500 mb-4">Send us a message and our team will get back to you.</p>
                <ContactForm data={data} onSent={(r) => setData((d) => ({ ...d, requests: [r, ...d.requests] }))} />
              </section>
              {data.requests.length > 0 && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="hc-support-my-requests">
                  <h2 className="text-base font-bold text-[#363636] mb-3">My Requests</h2>
                  <ul className="space-y-3">
                    {data.requests.map((r) => (
                      <li key={r.id} className="flex items-start gap-3" data-testid={`hc-support-request-${r.id}`}>
                        <CheckCircle2 className={`w-5 h-5 shrink-0 ${r.status === 'resolved' ? 'text-green-600' : 'text-gray-300'}`} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-sm text-[#363636]">{r.number} · {r.topic}</span>
                            <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${STATUS[r.status]}`} data-testid={`hc-support-request-status-${r.id}`}>{r.status === 'resolved' ? 'Resolved' : 'Open'}</span>
                          </div>
                          <p className="text-xs text-gray-500 line-clamp-2">{r.message}</p>
                          {(r.replies || []).map((x) => (
                            <div key={x.id} className={`mt-2 rounded-xl px-3 py-2 ${x.direction === 'inbound' ? 'bg-gray-100 ml-4' : 'bg-[#F6EAF5]'}`} data-testid={`hc-support-reply-${x.id}`}>
                              <div className={`text-[11px] font-semibold ${x.direction === 'inbound' ? 'text-gray-500' : 'text-[#922A8C]'}`}>{x.direction === 'inbound' ? 'You replied by text' : 'Help Desk reply'} · {new Date(x.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                              <p className="text-sm text-[#363636] whitespace-pre-line">{x.body}</p>
                            </div>
                          ))}
                          <p className="text-[11px] text-gray-400">{new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </aside>
          </div>
        )}
      </div>
    </HcLayout>
  );
}
